import React, { useState, useEffect, useMemo } from "react";
import { api } from "../../utils/api";
import { useParams } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import ProductForm from "./Forms/ProductForm";
import { useNavigate } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { DATA_GRID_STYLES } from "../../components/BasicUserInfo";
import { COLORS } from "../../utils/colors";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  annualRrpFromMonthly,
  applySalesforceLinkRrp,
  buildSalesforceSignupLinks,
  monthlyFromAnnualRrp,
  salesforceLinkKey,
} from "../../utils/salesforceSignupLinks";
import { showSuccessToast, showErrorToast } from "../../utils/toast";

const ProductSingle = () => {
  let navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [editProductState, setEditProductState] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteConfirmationIsOpen, setDeleteConfirmationIsOpen] = useState(false);
  const [linkRrpOverrides, setLinkRrpOverrides] = useState({});
  const [lockedLinksByKey, setLockedLinksByKey] = useState({});
  const [loadingSalesforceLinks, setLoadingSalesforceLinks] = useState(false);
  const [copyingLinkKey, setCopyingLinkKey] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await api("v1/product/" + params.idProduct, "GET", null);
        if (response.data.success) {
          // console.log(response.data.data.partnerCompany);
          setSelectedProduct(response.data.data.product);
          setLinkRrpOverrides({});
          setLoading(false);
        }
      } catch (error) {
        console.error("API error:", error);
      }
    };

    fetchProductDetails();
  }, []);

  const handleDeleteProductConfirm = async () => {
    setLoading(true);
    try {
      const response = await api("v1/product/" + selectedProduct._id, "DELETE", null);
      if (response.data.success) {
        console.log(response.data);
        // window.location.replace("/product");
      }
    } catch (error) {
      console.error("API error:", error);
    }
    toggleDisplayDeleteConfirm();
    setLoading(false); //once api call is finished.
  };

  const toggleDisplayDeleteConfirm = () => {
    setDeleteConfirmationIsOpen(!deleteConfirmationIsOpen);
  };

  const editProduct = () => {
    setEditProductState((prevValue) => !prevValue);
    // setOpenModal(true);
  };

  const reloadProductDetail = (updateCompanyDetail) => {
    setEditProductState((prevValue) => !prevValue);
    setSelectedProduct(updateCompanyDetail);
    setLinkRrpOverrides({});
    setLockedLinksByKey({});
  };

  console.log("editProductState", editProductState);

  const salesforceLinks = useMemo(
    () => (selectedProduct ? buildSalesforceSignupLinks(selectedProduct) : []),
    [selectedProduct]
  );

  useEffect(() => {
    if (!selectedProduct?._id) return;
    if (sessionStorage.getItem("selectedPortal") !== "Administrator") return;

    let cancelled = false;
    const loadLockedLinks = async () => {
      setLoadingSalesforceLinks(true);
      try {
        const response = await api(
          `v1/selfsignup/salesforce-links/${selectedProduct._id}`,
          "POST",
          {}
        );
        if (cancelled || !response?.data?.success) return;
        const next = {};
        for (const row of response.data.links || []) {
          next[salesforceLinkKey(row)] = row;
        }
        setLockedLinksByKey(next);
      } catch (error) {
        console.error("Failed to load Salesforce signup links:", error);
        showErrorToast("Could not generate Salesforce signup links");
      } finally {
        if (!cancelled) setLoadingSalesforceLinks(false);
      }
    };

    loadLockedLinks();
    return () => {
      cancelled = true;
    };
  }, [selectedProduct?._id]);

  const copySalesforceLink = async (link, liveLink) => {
    const key = salesforceLinkKey(link);
    const requestedRrp = liveLink.rrp;
    if (requestedRrp == null || Number.isNaN(requestedRrp)) {
      showErrorToast("Enter a valid RRP before copying");
      return;
    }

    setCopyingLinkKey(key);
    try {
      let url = lockedLinksByKey[key]?.url;
      const storedRrp = lockedLinksByKey[key]?.rrp;
      const needsNewLink =
        !url || storedRrp == null || Number(storedRrp) !== Number(requestedRrp);

      if (needsNewLink) {
        const response = await api(
          `v1/selfsignup/salesforce-links/${selectedProduct._id}`,
          "POST",
          {
            rrp: requestedRrp,
            vehicleType: link.vehicleType || null,
            lockInPeriodMonths:
              liveLink.lockInPeriodMonths ?? selectedProduct.lockInPeriodMonths ?? null,
          }
        );
        const created = response?.data?.links?.[0];
        if (!response?.data?.success || !created?.url) {
          throw new Error(response?.data?.message || "Failed to create locked signup link");
        }
        url = created.url;
        setLockedLinksByKey((prev) => ({
          ...prev,
          [key]: created,
        }));
      }

      await navigator.clipboard.writeText(url);
      showSuccessToast("Salesforce link copied");
    } catch (error) {
      console.error("Failed to copy Salesforce link:", error);
      showErrorToast("Could not copy link");
    } finally {
      setCopyingLinkKey(null);
    }
  };

  const formatMoney = (value) => {
    if (value == null || Number.isNaN(value)) return "—";
    return `$${Number(value).toFixed(2)}`;
  };

  const setLinkAnnualRrp = (link, annualValue) => {
    setLinkRrpOverrides((prev) => ({
      ...prev,
      [salesforceLinkKey(link)]: annualValue,
    }));
  };

  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);

    return formattedDate;
  }

  return (
    selectedProduct && (
      <div className="page partner-user-single w-full flex flex-column min-h-h-screen p-8">
        {loading && <GlobalLoadingSpinner />}

        <div className="pageTitle w-full flex flex-row justify-between">
          <h1>Product Details</h1>
          <div>
            <IconButton onClick={() => navigate(-1)} sx={{ border: "1px solid grey" }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>
        <h3 className="font-semibold mb-4">{selectedProduct.name}</h3>
        {/* Product Single Content */}
        <div className="w-[600px]">
          <div className={DATA_GRID_STYLES}>
            <span>Name</span>
            <span> {selectedProduct.name}</span>
            <span>Type</span>
            <span> {selectedProduct.type.toUpperCase()}</span>

            {selectedProduct.type == "package" && (
              <>
                <span>Subscription Duration</span>
                <span>
                  {selectedProduct.subscriptionDuration}{" "}
                  {parseInt(selectedProduct.subscriptionDuration) === 1 ? "Month" : "Months"}
                </span>
              </>
            )}

            {selectedProduct.type == "package" && (
              <>
                <span>Package Count</span>
                <span>{selectedProduct.packageCount}</span>
              </>
            )}
            <span>Price</span>
            <span> ${selectedProduct.price}</span>
            {/* <span>Redemption Price</span> */}
            {/* <span> ${selectedProduct.redemptionPrice}</span> */}
            <span>RRP</span>
            <span>${selectedProduct.rrp}</span>
            <span>Required Fields</span>
            <span>{selectedProduct.requiredFields.join(", ")}</span>
            <span>ACTIVE</span>
            <span> {selectedProduct.active ? "Yes" : "No"}</span>
            <span>Created On</span>
            <span> {formatDate(selectedProduct.createdOnUTC)}</span>
          </div>

          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <div className="actionItems flex gap-4">
              <Button
                size="large"
                sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                className="self-start m-4"
                variant="contained"
                onClick={editProduct}
              >
                Edit Product
              </Button>
              <Button
                size="large"
                className="self-start bg-red-600 m-4"
                variant="contained"
                color="error"
                onClick={() => {
                  toggleDisplayDeleteConfirm(true);
                }}
              >
                Delete Product
              </Button>
            </div>
          )}

          {sessionStorage.getItem("selectedPortal") == "Administrator" && salesforceLinks.length > 0 && (
            <div className="mt-6 mb-4">
              <h4 className="font-semibold mb-2">Salesforce signup links</h4>
              <p className="text-sm text-gray-600 mb-3">
                Each variant is a locked key/token URL. Product, RRP, vehicle type, and lock-in
                cannot be changed in the URL. Customer details (email, name, phone) can still be
                appended. Changing RRP here generates a new locked link — it does not update the
                product catalog.
              </p>
              {loadingSalesforceLinks && (
                <p className="text-sm text-gray-500 mb-3">Generating locked signup links…</p>
              )}
              <div className="flex flex-col gap-3">
                {salesforceLinks.map((link) => {
                  const key = salesforceLinkKey(link);
                  const override = linkRrpOverrides[key];
                  const annualRrp =
                    override === "" || override == null ? link.rrp : Number(override);
                  const liveLink = applySalesforceLinkRrp(link, annualRrp);
                  const monthly = liveLink.monthly;
                  const catalogMonthly = link.monthly ?? monthlyFromAnnualRrp(link.rrp);
                  const locked = lockedLinksByKey[key];
                  const displayUrl =
                    locked?.rrp != null && Number(locked.rrp) === Number(liveLink.rrp)
                      ? locked.url
                      : null;
                  const lockInLabel =
                    liveLink.lockInPeriodMonths != null
                      ? `${liveLink.lockInPeriodMonths}-month lock-in`
                      : null;

                  return (
                    <div key={key} className="p-3 bg-gray-50 rounded border">
                      <div className="text-sm font-medium mb-3">{link.label}</div>
                      <div className="flex flex-col sm:flex-row gap-3 mb-3">
                        <TextField
                          label="Monthly"
                          size="small"
                          type="number"
                          value={monthly == null || Number.isNaN(monthly) ? "" : monthly}
                          onChange={(event) => {
                            const nextMonthly = event.target.value;
                            if (nextMonthly === "") {
                              setLinkAnnualRrp(link, "");
                              return;
                            }
                            setLinkAnnualRrp(link, annualRrpFromMonthly(nextMonthly) ?? "");
                          }}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                        <TextField
                          label="Annual RRP"
                          size="small"
                          type="number"
                          value={
                            liveLink.rrp == null || Number.isNaN(liveLink.rrp) ? "" : liveLink.rrp
                          }
                          onChange={(event) => setLinkAnnualRrp(link, event.target.value)}
                          inputProps={{ min: 0, step: "1" }}
                        />
                      </div>
                      <p className="text-sm text-gray-700 mb-2">
                        {formatMoney(monthly)} / month · {formatMoney(liveLink.rrp)} / year
                        {catalogMonthly != null && link.rrp != null
                          ? ` (catalog ${formatMoney(catalogMonthly)} / month · ${formatMoney(link.rrp)} / year)`
                          : ""}
                        {lockInLabel ? ` · ${lockInLabel}` : ""}
                      </p>
                      <p className="text-xs text-gray-600 break-all mb-2">
                        {displayUrl ||
                          (liveLink.rrp == null
                            ? "Enter an RRP to generate a locked link."
                            : "Click Copy link to generate a locked key/token URL for this RRP.")}
                      </p>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ backgroundColor: COLORS.priorityBlue.hex }}
                        onClick={() => copySalesforceLink(link, liveLink)}
                        disabled={liveLink.rrp == null || copyingLinkKey === key}
                      >
                        {copyingLinkKey === key ? "Generating…" : "Copy link"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* // Edit Modal HTML */}

        <ModalPopup
          showModal={editProductState}
          handleShowModal={editProduct}
          popupTitle={"Edit Product"}
          popupContent={<ProductForm product={selectedProduct} callback={reloadProductDetail} />}
          cancelButtonText=""
          confirmButtonText=""
          confirmCallbackAction=""
        />

        {/* Delete Product Confimation Modal */}
        <ModalPopup
          showModal={deleteConfirmationIsOpen}
          handleShowModal={toggleDisplayDeleteConfirm}
          popupTitle={"Delete Product"}
          popupContent={
            <div>
              Are you sure you want to delete product:
              <strong>
                <em> {selectedProduct.name} </em>
              </strong>
              ?
            </div>
          }
          cancelButtonText={"Cancel"}
          confirmButtonText={"Delete"}
          confirmCallbackAction={handleDeleteProductConfirm}
        />
      </div>
    )
  );
};

export default ProductSingle;
